import axios from 'axios';
import moment from 'moment';
import 'moment/locale/es';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import DataTable from '../components/DataTable';

moment.locale('es');

const ListarInformesContractuales = () => {
  const { id } = useParams();
  const [informes, setInformes] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const generarYDescargarInforme = async (idInforme, documentoInstructor, rp, fechaInicio, mes) => {
    try {
      const response = await axios.get(`/api/informe-contractual/generar-docx/${idInforme}`, {
        responseType: 'blob',
      });

      const anioInicio = fechaInicio ? moment(fechaInicio).format('YYYY') : 'N-A';
      const mesFormatted = mes ? String(mes).toUpperCase() : 'SIN_MES';
      const rpValue = rp !== 'N/A' ? rp : 'SINRP';
      const docInstr = documentoInstructor !== 'N/A' ? documentoInstructor : 'SIN_DOC';

      const fileName = `GC_${docInstr}_${rpValue}_${mesFormatted}_${anioInicio}.docx`;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error al generar el informe:', error.message);
      Swal.fire('Error', 'No se pudo generar el informe.', 'error');
    }
  };

  const deleteInforme = async (informeId) => {
    try {
      const result = await Swal.fire({
        title: '¿Está seguro?',
        text: '¡Esta acción eliminará el informe de forma permanente!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar',
      });

      if (result.isConfirmed) {
        const { data } = await axios.delete(`/api/informecontractual/delete/${informeId}`);
        await getData();
        Swal.fire({
          icon: 'success',
          title: data.message || 'Informe eliminado',
          showConfirmButton: false,
          timer: 1500,
        });
      }
    } catch (error) {
      console.error('Error al eliminar el informe:', error.message);
      Swal.fire('Error', 'No se pudo eliminar el informe.', 'error');
    }
  };

  const getData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`/api/informecontractual/listbycontrato/${id}`);
      let data = response.data.informes || [];

      // Ordenar por fecha asc y luego invertir para mostrar reciente primero
      data.sort((a, b) => new Date(a.fechaInforme) - new Date(b.fechaInforme));

      const dataConItem = data.map((informe, index) => ({
        ...informe,
        item: index + 1,
      }));

      const informesConFunciones = dataConItem.reverse().map((informe) => {
        const documentoInstructor = informe.documentoInstructor ?? 'N/A';
        const rp = informe.rp ?? 'N/A';
        const fechaInicio = informe.fechaInicio ?? null;
        const mes = informe.mes ?? null;

        return {
          ...informe,
          fechaInforme: informe.fechaInforme
            ? moment.utc(informe.fechaInforme).add(5, 'hours').format('DD/MM/YYYY')
            : '-',

          funciones: (
            <div className="d-flex gap-2 flex-wrap justify-content-end">
              {/* Descargar Word */}
              <button
                className="btn btn-dark btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={() =>
                  generarYDescargarInforme(informe._id, documentoInstructor, rp, fechaInicio, mes)
                }
                title="Descargar Informe Contractual"
                type="button"
              >
                <i className="fa-solid fa-file-word"></i>
              </button>

              {/* Editar */}
              <button
                className="btn btn-warning btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={() => navigate(`/instructor/informecontractual/editar/${informe._id}`)}
                title="Editar Informe"
                type="button"
              >
                <i className="fa-solid fa-pen-to-square"></i>
              </button>

              {/* Eliminar */}
              <button
                className="btn btn-danger btn-sm d-inline-flex align-items-center justify-content-center"
                style={{ width: 34, height: 34 }}
                onClick={() => deleteInforme(informe._id)}
                title="Eliminar Informe"
                type="button"
              >
                <i className="fa-solid fa-trash"></i>
              </button>
            </div>
          ),
        };
      });

      setInformes(informesConFunciones);
    } catch (error) {
      console.error('Error al obtener los informes:', error.message);
      setInformes([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    if (id) getData();
  }, [id, getData]);

  const columns = useMemo(
    () => [
      { Header: 'Item', accessor: 'item' },
      { Header: 'Ciudad', accessor: 'ciudad' },
      { Header: 'Fecha del Informe', accessor: 'fechaInforme' },
      { Header: 'Mes', accessor: 'mes' },
      { Header: 'Año', accessor: 'anio' },
      { Header: 'N° Planilla', accessor: 'numeroPlanilla' },
      { Header: 'Periodo', accessor: 'periodoPlanilla' },
      { Header: 'Operador', accessor: 'operadorPlanilla' },
      { Header: 'Viajes', accessor: 'totalDesplazamientos' },
      { accessor: 'funciones' },
    ],
    []
  );

  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          {/* Header */}
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">Informes mensuales registrados</h5>
                <small className="text-secondary">
                  Descarga, edición y control de informes contractuales.
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => navigate(-1)}
                  title="Volver"
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>

                  Volver
                </button>

                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => getData()}
                  title="Actualizar"
                  disabled={loading}
                >
                  <i className={`fa-solid ${loading ? 'fa-spinner fa-spin' : 'fa-rotate'} me-2`}></i>
                  Actualizar
                </button>
              </div>
            </div>
          </div>

          {/* Body */}
          <div>
            {loading ? (
              <div className="text-center text-secondary py-5">
                <div className="spinner-border" role="status" aria-hidden="true"></div>
                <div className="mt-3">Cargando informes…</div>
              </div>
            ) : Array.isArray(informes) && informes.length > 0 ? (
              <div className="overflow-hidden">
                <DataTable columns={columns} data={informes} />
              </div>
            ) : (
              <div className="text-center text-secondary py-5">
                <div className="display-6 mb-2">🐾</div>
                <div className="fw-semibold">No hay informes registrados</div>
                <div className="small">No se encontraron informes para este contrato.</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListarInformesContractuales;
